import { GroupNotesSheet } from '../components/GroupNotesSheet';
import { DrillSuggest } from '../components/Drills';
import { createPortal } from 'react-dom';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { DayStrip } from '../components/DayStrip';
import { WeekView, type Week } from '../components/WeekView';
import { MonthSheet, type MonthSession } from '../components/MonthSheet';
import { addDaysToKey, dayRange, fmtLongDay, fmtTime, toCoachDateKey } from '../lib/time';
import { alert, confirm, haptic, openExternal } from '../lib/telegram';
import { PLAYER_SEES_SHARED_NOTES } from '../lib/flags';
import { guideFor } from '../components/AxisSheet';
import { axisName, fmtMoney, getLang, translate, useT } from '../lib/i18n';
import { CancelSheet } from '../components/CancelSheet';
import { MoveSheet } from '../components/MoveSheet';
import { VisLabel } from '../components/Vis';
import { GrowArea } from '../components/GrowArea';
import { addedJustNow, fetchSeatState, RemoveSheet, removeErrorText, removeRefusal, type SeatMoves } from '../components/RemoveSheet';
import { MethodPicker, methodLabel, rememberMethod, useMethod } from '../components/MethodPicker';
import { PaySwitchSheet } from '../components/PaySwitch';
import { EditPaymentSheet, LessonPaySheet } from '../components/LessonPayment';
import { cardState, dayToCollect, firstBooked, groupSummary, minutesLeft, minutesUntil, progress } from '../lib/today-card';
import { browserStore, dropLocal, keepLocal, lastScores, rateKey, readLocal, restoreFrom, takeLast } from '../lib/live-lesson';
import { LiveHeader } from '../components/LiveHeader';
import { ShareStoryButton } from '../components/ShareStory';
import { storyWeekFor } from '../lib/story-week';
import { attendanceChange, attendanceOpen, messageHref, payLine, ROUND_OFF } from '../lib/lesson-hub';

/**
 * The coach's home screen, schedule-led.
 *
 * A rides app leads with earnings because for a driver, earnings ARE the
 * job status. A coach opens this between sessions to answer "who's next",
 * and asks about money on a Sunday evening. So the next session is the
 * biggest thing on screen, today's list sits under it, and money is a
 * two-number strip that expands when it's actually the question.
 */

const BASE = import.meta.env.VITE_API_URL ?? '';

/**
 * 0 to 10 in words, so a number a coach picks in five seconds still
 * means the same thing to the next coach who reads it.
 */
type RatingWordKey = 'co.rate.w0' | 'co.rate.w1' | 'co.rate.w3'
  | 'co.rate.w5' | 'co.rate.w7' | 'co.rate.w9';

function ratingWord(n: number): RatingWordKey {
  return n <= 0 ? 'co.rate.w0'
    : n <= 2 ? 'co.rate.w1'
    : n <= 4 ? 'co.rate.w3'
    : n <= 6 ? 'co.rate.w5'
    : n <= 8 ? 'co.rate.w7'
    : 'co.rate.w9';
}
let token = '';
export const setDashToken = (t: string) => { token = t; };

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    // The type only when there is a body: a DELETE that sends none is
    // refused as an empty JSON body (lib/api.ts says the same).
    headers: { ...(init.body == null ? {} : { 'content-type': 'application/json' }),
      authorization: `Bearer ${token}`, ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `http_${res.status}`);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

interface Booking {
  bookingId: string;
  clientId: string; name: string; username: string | null; phone: string | null;
  status: string; paymentMode: 'credits' | 'cash'; amountDue: number | null;
  paidAt: string | null; botStarted: boolean; bookedAt?: string;
  clientNote?: string | null; coachNote?: string | null;
  workingOn?: string | null;
  /** A rating kept as a draft, and when a Save made during the lesson shows to the player. */
  ratingDraft?: boolean; ratingPublishAt?: string | null;
  /** What is saved for this player: the coach's ratings, and whether a note / homework was written. */
  ratings?: { name: string; name_ru: string | null; score: number }[];
  hasNote?: boolean; hasHomework?: boolean;
  /** Paid partly in credits (credits and what they are worth), cash received so far, and a credit's price. */
  creditsCharged?: number | null; creditValue?: number | null; received?: number | null; perCredit?: number | null;
  /** Cancelled by the client inside the free-cancellation window: still counts, often still owed. */
  lateCancel?: boolean;
  /** On credits and cancelled late: whether a credit was actually taken for it (slice 2). */
  creditTaken?: boolean | null;
}
interface Session {
  id: string; starts_at: string; ends_at: string; status: string;
  location: string | null; title: string; bookings: Booking[];
  /** A pair or group's notes sheet: a draft, and when a confirmed one shows to the players. */
  notesDraft?: boolean; notesPublishAt?: string | null;
  /** The note for everyone on a pair or group, once saved. */
  commonNote?: boolean;
}
interface Day { date: string; earned: number; collected: number; sessions: number }

const REASONS: [string, 'co.reason.rain' | 'co.reason.noCourt' | 'co.reason.illness'
  | 'co.reason.emergency' | 'co.reason.other'][] = [
  ['rain', 'co.reason.rain'], ['court', 'co.reason.noCourt'], ['illness', 'co.reason.illness'],
  ['emergency', 'co.reason.emergency'], ['other', 'co.reason.other'],
];

export function Dashboard({ onOpenClient, jumpDay }: {
  onOpenClient?: (id: string, name?: string) => void;
  /** Show this day (set by «Первые шаги»); `at` makes the same day jump again. */
  jumpDay?: { day: string; at: number } | null;
}) {
  const t = useT();
  const [day, setDay] = useState(() => jumpDay?.day ?? toCoachDateKey(new Date()));
  // «День / Неделя», remembered on this device.
  const [view, setViewState] = useState<'day' | 'week'>(() => {
    try { return localStorage.getItem('pd.cal.view') === 'week' ? 'week' : 'day'; } catch { return 'day'; }
  });
  const setView = (v: 'day' | 'week') => {
    setViewState(v);
    try { localStorage.setItem('pd.cal.view', v); } catch { /* private mode: just this time */ }
  };
  // A block tapped in the week: that day, scrolled to its card.
  const [focus, setFocus] = useState<string | null>(null);
  // «Занятие вживую»: the lesson on court, as it was when its Today card
  // was tapped. The sheet stays with it when the next lesson starts.
  const [liveLesson, setLiveLesson] = useState<Session | null>(null);
  useEffect(() => { if (jumpDay) { setDay(jumpDay.day); setView('day'); } }, [jumpDay?.at]);
  const fetchWeek = useCallback((from: string) => api<Week>(`/api/coach/week?from=${from}`), []);
  const [picking, setPicking] = useState(false);
  const todayKey = toCoachDateKey(new Date());
  /**
   * Two months back and a month ahead, stretched to reach a date picked
   * from the month view, so the strip always has the selected day in it.
   */
  const days = useMemo(() => {
    const from = addDaysToKey(todayKey, -60), to = addDaysToKey(todayKey, 30);
    return dayRange(
      day < from ? addDaysToKey(day, -7) : from,
      day > to ? addDaysToKey(day, 7) : to);
  }, [todayKey, day]);
  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [disrupting, setDisrupting] = useState(false);
  const [requestTick, setRequestTick] = useState(0);
  // Days with a 🔥 time, for the strip; refreshed when one is marked.
  const [fireDays, setFireDays] = useState<Set<string>>(new Set());
  const loadFire = useCallback(() => api<{ times: string[] }>('/api/coach/hot-times')
    .then((r) => setFireDays(new Set(r.times.map((x) => toCoachDateKey(new Date(x))))))
    .catch(() => {}), []);
  useEffect(() => { void loadFire(); }, [loadFire]);

  const isToday = day === todayKey;
  const isPast = day < todayKey;
  // The clock the Today card reads: every 30 s while today is on screen,
  // and on coming back to the app, so it counts down and changes state by itself.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isToday) return;
    setNow(Date.now());
    const i = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(i);
  }, [isToday]);

  const fetchMonth = useCallback((from: string, to: string) =>
    api<MonthSession[]>(`/api/coach/schedule?from=${from}&to=${to}&late=1`), []);

  const load = useCallback(async () => {
    setSessions(null);
    setSessions(await api<Session[]>(`/api/coach/schedule?from=${day}&to=${day}&late=1`));
  }, [day]);
  /**
   * After something done on a card: fetched again with the list left on
   * screen. Clearing it first put the skeleton up for a moment, the page
   * got shorter and the scroll went back to the top.
   */
  const refresh = useCallback(async () => {
    const at = window.scrollY;
    const fresh = await api<Session[]>(`/api/coach/schedule?from=${day}&to=${day}&late=1`).catch(() => null);
    if (fresh === null) return;
    setSessions(fresh);
    requestAnimationFrame(() => { if (Math.abs(window.scrollY - at) > 1) window.scrollTo(0, at); });
  }, [day]);

  useEffect(() => { load().catch(() => setSessions([])); }, [load]);
  useEffect(() => {
    if (!focus || sessions === null || view !== 'day') return;
    // The day's sessions may still be the last day's: wait for this one's card.
    const el = document.getElementById(`sess-${focus}`);
    if (!el) return;
    setFocus(null);
    requestAnimationFrame(() => {
      el.scrollIntoView({ block: 'center' });
      el.classList.add('tl__item--focus');
      setTimeout(() => el.classList.remove('tl__item--focus'), 1700);
    });
  }, [focus, sessions, view]);
  useEffect(() => { if (!focus) return; const i = setTimeout(() => setFocus(null), 4000); return () => clearTimeout(i); }, [focus]);
  // The app left open in Telegram: back to it, the day is fetched again,
  // so no card acts on a booking changed in the meantime.
  useEffect(() => {
    const back = () => { if (document.visibilityState === 'visible') { setNow(Date.now()); void refresh(); } };
    document.addEventListener('visibilitychange', back);
    return () => document.removeEventListener('visibilitychange', back);
  }, [refresh]);
  // Late cancellations are listed so the coach sees who owes, but they
  // are not lessons anyone is coming to: the counts leave them out.
  const coming = (sessions ?? []).map((s) => ({ ...s, bookings: s.bookings.filter((b) => !b.lateCancel) }));
  const booked = coming.filter((s) => s.bookings.length > 0);
  const lesson = liveLesson && ((sessions ?? []).find((s) => s.id === liveLesson.id) ?? liveLesson);
  const doneCount = booked.filter((s) => new Date(s.ends_at).getTime() < now).length;

  return (
    <>
      {isToday && view === 'day' && (
        <TodayCard sessions={sessions} now={now} onOpen={(s, onCourt) => {
          haptic.tap();
          if (onCourt) setLiveLesson(s); else setFocus(s.id);
        }} />
      )}

      <PackageRequests key={requestTick} onChanged={() => setRequestTick((n) => n + 1)} />
      <PendingMatches onChanged={load} />

      <div className="calview" role="tablist">
        {(['day', 'week'] as const).map((v) => (
          <button key={v} role="tab" aria-pressed={view === v}
            onClick={() => { if (view === v) return; haptic.tap(); setView(v); if (v === 'day') void refresh(); }}>
            {t(v === 'day' ? 'wk.day' : 'wk.week')}
          </button>
        ))}
      </div>

      {view === 'week' ? (
        <WeekView fetchWeek={fetchWeek} initialDay={day}
          headerAction={(from) => <ShareStoryButton api={api} compact week={storyWeekFor(from, toCoachDateKey(new Date())) ?? undefined} />}
          onOpenSession={(k, id) => { setFocus(id); setView('day'); if (k === day) void refresh(); else setDay(k); }} />
      ) : (<>
      <div className="stripwrap">
        <DayStrip days={days} selected={day} emptyDays={new Set()} followSelected fireDays={fireDays}
          onSelect={(k) => { haptic.tap(); setDay(k); }} />
        <div className="stripwrap__cal">
          <button aria-label={t('co.month.open')} onClick={() => { haptic.tap(); setPicking(true); }}>
            <CalendarIcon />
          </button>
        </div>
      </div>

      <div className={`dayhead${isPast ? ' dayhead--past' : ''}`}>
        <div>
          <div className="dayhead__title">{isToday ? t('co.today.day') : fmtLongDay(new Date(`${day}T12:00:00`))}</div>
          <div className="dayhead__sub">
            {sessions === null ? ' ' : booked.length === 0 ? t('co.today.nothing')
              : t('co.today.count', {
                  n: booked.length, sessions: t.word('co.today.sessions', booked.length),
                  done: isToday && doneCount ? t('co.today.done', { n: doneCount }) : '',
                })}
          </div>
        </div>
        {/* A day that is over cannot be called off. */}
        {booked.length > 0 && !isPast && (
          <button className="dayhead__action" onClick={() => setDisrupting(true)}>{t('co.today.cancelDay')}</button>
        )}
      </div>

      {sessions === null ? (
        <div className="list"><div className="skeleton" /><div className="skeleton" /></div>
      ) : sessions.length === 0 ? (
        <div className="empty" style={{ padding: '32px 28px' }}>
          <div className="empty__lead">{t('co.today.freeDay')}</div>
          {t('co.today.freeDayHint')}
        </div>
      ) : (
        <div className="tl">
          {sessions.map((s) => <SessionCard key={s.id} session={s} onChanged={refresh} onOpenClient={onOpenClient} />)}
        </div>
      )}

      {!isPast && <OpenTimes key={day} day={day} onChanged={loadFire} />}
      {/* The free times of this week or the next, as an Instagram story. */}
      <ShareStoryButton api={api} />
      </>)}

      {picking && (
        <MonthSheet selected={day} fetchMonth={fetchMonth}
          onPick={(k) => { setDay(k); setPicking(false); }}
          onClose={() => setPicking(false)} />
      )}
      {disrupting && <DisruptSheet day={day} onClose={() => setDisrupting(false)} onDone={load} />}
      {/* The same sheets as the session card's «Оценить», with the lesson's header
          and «Панель занятия» on top. The lesson is the one tapped, as the day was
          last fetched: a mark or a payment made here shows at once. */}
      {lesson && (() => {
        const close = () => { setLiveLesson(null); void refresh(); };
        const hub = <LessonHub session={lesson} onChanged={() => void refresh()}
          onOpenClient={onOpenClient && ((id, name) => { setLiveLesson(null); onOpenClient(id, name); })} />;
        const people = lesson.bookings.filter((b) => !b.lateCancel);
        return people.length >= 2 ? (
          <GroupNotesSheet sessionId={lesson.id} live hub={hub} onClose={close} onDone={() => void refresh()} />
        ) : people[0] ? (
          <RatingSheet key={people[0].bookingId} booking={people[0]} hub={hub}
            live={{ startsAt: lesson.starts_at, endsAt: lesson.ends_at, who: people[0].name }}
            onClose={close} onDone={() => void refresh()} />
        ) : null;
      })()}
    </>
  );
}

/**
 * The day's open times, as players see them on Book, under the sessions.
 *
 * Two things a coach does with a free hour, from the same row: offer it
 * harder, or take it off the table. The switch above the chips says which
 * a tap does — 🔥 marks the time as one the coach wants filled (same
 * price, nothing is sent), 🔒 blocks it, in the word «Закрыть время»
 * already uses for the same thing. A blocked time stays in the row,
 * dimmed, and a tap opens it back up whichever mode is on: undoing should
 * never need a detour through the switch.
 */
type SlotMode = 'hot' | 'block';
const MODE_KEY = 'pd.slot.mode';
/** The last mode on this device. 🔥 for anyone who has never chosen. */
const readMode = (): SlotMode => {
  try { return localStorage.getItem(MODE_KEY) === 'block' ? 'block' : 'hot'; } catch { return 'hot'; }
};

interface OpenTime { startsAt: string; endsAt: string; hot: boolean; blockId?: string }

function OpenTimes({ day, onChanged }: { day: string; onChanged: () => void }) {
  const t = useT();
  const [times, setTimes] = useState<OpenTime[] | null>(null);
  const [open, setOpen] = useState<OpenTime | null>(null);
  const [mode, setMode] = useState<SlotMode>(readMode);
  /** The chip being saved: disabled until the server has answered. */
  const [busy, setBusy] = useState<string | null>(null);
  const load = useCallback(() => api<{ times: OpenTime[] }>(`/api/coach/open-times?date=${day}`)
    .then((r) => setTimes(r.times)).catch(() => setTimes([])), [day]);
  useEffect(() => { void load(); }, [load]);

  function pickMode(m: SlotMode) {
    haptic.tap();
    setMode(m);
    try { localStorage.setItem(MODE_KEY, m); } catch { /* private mode: just this time */ }
  }

  /**
   * Shown at once, saved behind: the round trip and the recount of the
   * day's open times take a second or two, long enough for a tap to look
   * dead. On failure the chip goes back and the coach is told.
   */
  async function save(hot: boolean) {
    if (!open) return;
    const at = open.startsAt;
    const set = (v: boolean) => setTimes((cur) => cur && cur.map((x) => (x.startsAt === at ? { ...x, hot: v } : x)));
    set(hot);
    setOpen(null);
    haptic.success();
    try {
      await api('/api/coach/hot-times', { method: 'PUT', body: JSON.stringify({ startsAt: at, date: day, hot }) });
      onChanged();
    } catch (err: any) {
      set(!hot);
      haptic.error();
      await alert(err.message === 'not_open' ? t('co.hot.notOpen') : t('co.failed', { message: err.message }));
    }
    void load();
  }

  /**
   * Block one slot: an exception over exactly that time, no reason — the
   * same blocks «Закрыть время» makes, so players, the week grid and the
   * calendar feeds all already know about it.
   */
  async function block(x: OpenTime) {
    setBusy(x.startsAt);
    try {
      const r = await api<{ block: { id: string } }>('/api/coach/blocks', {
        method: 'POST', body: JSON.stringify({ starts_at: x.startsAt, ends_at: x.endsAt, reason: null }),
      });
      setTimes((cur) => cur && cur.map((s) => (s.startsAt === x.startsAt ? { ...s, hot: false, blockId: r.block.id } : s)));
      haptic.success();
      onChanged();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.failed', { message: err.message }));
    }
    setBusy(null);
    void load();
  }

  /** Open a blocked slot back up: delete that block, nothing else. */
  async function reopen(x: OpenTime) {
    setBusy(x.startsAt);
    try {
      await api(`/api/coach/blocks/${x.blockId}`, { method: 'DELETE' });
      setTimes((cur) => cur && cur.map((s) => (s.startsAt === x.startsAt ? { ...s, blockId: undefined } : s)));
      haptic.success();
      onChanged();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.failed', { message: err.message }));
    }
    setBusy(null);
    void load();
  }

  function tap(x: OpenTime) {
    haptic.tap();
    if (x.blockId) { void reopen(x); return; }
    if (mode === 'block') { void block(x); return; }
    setOpen(x);
  }

  if (times === null || times.length === 0) return null;
  return (
    <>
      <div className="section-title" id="open-times">{t('co.hot.openTimes')}</div>
      <div className="bseg hot-seg" role="radiogroup" aria-label={t('co.hot.openTimes')}>
        <button role="radio" aria-checked={mode === 'hot'} onClick={() => pickMode('hot')}>{t('co.hot.modeHot')}</button>
        <button role="radio" aria-checked={mode === 'block'} onClick={() => pickMode('block')}>{t('co.hot.modeBlock')}</button>
      </div>
      <div className="hot-hint">{mode === 'block' ? t('co.hot.hintBlock') : t('co.hot.hint')}</div>
      <div className="hot-chips">
        {times.map((x) => (
          <button key={x.startsAt} disabled={busy === x.startsAt}
            className={`hot-chip${x.hot ? ' hot-chip--on' : ''}${x.blockId ? ' hot-chip--blocked' : ''}`}
            aria-label={x.blockId ? `${fmtTime(x.startsAt)} — ${t('co.hot.blocked')}` : undefined}
            onClick={() => tap(x)}>
            {x.blockId
              ? <span aria-hidden="true">🔒</span>
              : x.hot && <span aria-hidden="true">🔥</span>}{fmtTime(x.startsAt)}
          </button>
        ))}
      </div>
      {open && createPortal(
        <div className="sheet" onClick={() => setOpen(null)}>
          <div className="sheet__panel" onClick={(e) => e.stopPropagation()}>
            <div className="sheet__title">{t('co.hot.title')}</div>
            <div className="hot-q">{open.hot ? t('co.hot.isMarked', { time: fmtTime(open.startsAt) }) : t('co.hot.markQ', { time: fmtTime(open.startsAt) })}</div>
            <div className="card__when">{open.hot ? t('co.hot.whatOff') : t('co.hot.what')}</div>
            <div className="hot-actions">
              <button className="hot-btn" onClick={() => setOpen(null)}>{t('co.set.cancel')}</button>
              {open.hot
                ? <button className="hot-btn hot-btn--off" onClick={() => save(false)}>{t('co.hot.unmark')}</button>
                : <button className="hot-btn hot-btn--on" onClick={() => save(true)}>{t('co.hot.mark')}</button>}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" /><path d="M3.5 10h17M8 3v4M16 3v4" />
    <circle cx="8.5" cy="14.5" r=".6" fill="currentColor" /><circle cx="12" cy="14.5" r=".6" fill="currentColor" />
    <circle cx="15.5" cy="14.5" r=".6" fill="currentColor" />
  </svg>
);

/**
 * The card at the top of today: the lesson on court (with how much of it
 * is left), else the next one, else the day's numbers and tomorrow's
 * first lesson. Tapping the lesson on court opens its sheet («Занятие
 * вживую»); tapping the next one scrolls to its card in the list. The
 * state is lib/today-card.ts.
 */
function TodayCard({ sessions, now, onOpen }: {
  sessions: Session[] | null; now: number; onOpen: (session: Session, onCourt: boolean) => void;
}) {
  const t = useT();
  const state = cardState(sessions, now);
  const todayKey = toCoachDateKey(new Date(now));
  // The end state's numbers: earned as the Money tab counts it, and the
  // first lesson tomorrow. Fetched again after anything done on a card.
  const [earned, setEarned] = useState<number | null>(null);
  const [tomorrow, setTomorrow] = useState<Session | null | undefined>(undefined);
  const done = state.kind === 'done';
  useEffect(() => {
    if (!done) return;
    let live = true;
    api<{ days: { date: string; earned: number }[] }>('/api/coach/stats')
      .then((r) => { if (live) setEarned(r.days.find((d) => d.date === todayKey)?.earned ?? 0); })
      .catch(() => {});
    const next = addDaysToKey(todayKey, 1);
    api<Session[]>(`/api/coach/schedule?from=${next}&to=${next}`)
      .then((r) => { if (live) setTomorrow(firstBooked(r)); })
      .catch(() => {});
    return () => { live = false; };
  }, [done, todayKey, sessions]);

  const who = (s: Session) => groupSummary(s.bookings.length)
    ? t('co.today.group', { n: s.bookings.length, players: t.word('co.today.players', s.bookings.length) })
    : s.bookings.map((b) => b.name).join(', ');

  if (state.kind === 'onCourt' || state.kind === 'next') {
    const hero = state.session;
    const onCourt = state.kind === 'onCourt';
    const group = groupSummary(hero.bookings.length);
    const focus = group ? undefined : hero.bookings.find((b) => b.workingOn)?.workingOn;
    const cash = hero.bookings.filter((b) => b.paymentMode === 'cash' && !b.paidAt)
      .reduce((sum, b) => sum + owedOf(b), 0);
    const open = () => onOpen(hero, onCourt);
    return (
      <div className="upnext upnext--tap" role="button" tabIndex={0}
        aria-label={t(onCourt ? 'co.live.open' : 'co.today.showSession', { time: fmtTime(hero.starts_at) })}
        onClick={open} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}>
        <div className="upnext__top">
          <span className="upnext__label">{onCourt ? t('co.today.onCourt') : t('co.today.nextUp')}</span>
          <span className="upnext__in">{onCourt
            ? t('co.today.until', { time: fmtTime(hero.ends_at) })
            : countdown(minutesUntil(hero.starts_at, now))}</span>
        </div>
        <div className="upnext__time">
          {fmtTime(hero.starts_at)}<small>– {fmtTime(hero.ends_at)}</small>
        </div>
        {onCourt && (
          <>
            <div className="upnext__bar" aria-hidden="true">
              <span style={{ width: `${Math.round(progress(hero, now) * 100)}%` }} />
            </div>
            <div className="upnext__left">{t('co.today.left', { n: minutesLeft(hero.ends_at, now) })}</div>
          </>
        )}
        <div className="upnext__people">
          <div className="upnext__faces">
            {hero.bookings.slice(0, 4).map((b) => <span key={b.bookingId} className="face">{initials(b.name)}</span>)}
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="upnext__names">{who(hero)}</div>
            <div className="upnext__where">{hero.title}{hero.location ? ` · ${hero.location}` : ''}</div>
          </div>
        </div>
        {focus && <FocusNote className="upnext__focus" text={focus} />}
        {cash > 0 && <span className="upnext__cash">{t('co.today.toCollect', { amount: cash })}</span>}
      </div>
    );
  }

  return (
    <div className="upnext">
      <div className="upnext__top"><span className="upnext__label">{t('co.today.nextUp')}</span></div>
      <div className="upnext__time upnext__time--none">
        {state.kind === 'loading' ? '…' : done ? t('co.today.allDone') : t('co.today.nothingToday')}
      </div>
      {state.kind === 'done' && (
        <>
          <div className="upnext__nums">
            <div><b>{state.taught}</b><span>{t('co.today.numTaught')}</span></div>
            <div><b>{earned === null ? '…' : fmtMoney(Math.round(earned))}</b><span>{t('co.today.numEarned')}</span></div>
            <div><b>{fmtMoney(Math.round(dayToCollect(sessions ?? [], now)))}</b><span>{t('co.today.numToCollect')}</span></div>
          </div>
          <div className="upnext__tomorrow">
            {tomorrow === undefined ? '…' : tomorrow
              ? t('co.today.tomorrow', { time: fmtTime(tomorrow.starts_at), who: who(tomorrow) })
              : t('co.today.tomorrowNone')}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * «Панель занятия», slices 1–3 (docs/design/lesson-hub.md): under the
 * lesson's header, one block per player — Пришёл / Не пришёл from the
 * start, the payment line, «Работаем над», Написать and Профиль — and
 * «+ Игрок» once. Every action is an existing endpoint: attendance,
 * «Получено» (LessonPaySheet, cash bookings only), add-client. A credits
 * lesson shows the payer's balance; the server settles it after the end.
 */
function LessonHub({ session, onChanged, onOpenClient }: {
  session: Session; onChanged: () => void; onOpenClient?: (id: string, name?: string) => void;
}) {
  const t = useT();
  const [paying, setPaying] = useState<Booking | null>(null);
  const [editing, setEditing] = useState<Booking | null>(null);
  const [adding, setAdding] = useState(false);
  const people = session.bookings.filter((b) => !b.lateCancel);
  return (
    <div className="hub">
      {people.map((b) => (
        <HubPlayer key={b.bookingId} session={session} booking={b} named={people.length > 1}
          onPay={() => { haptic.tap(); setPaying(b); }} onEdit={() => { haptic.tap(); setEditing(b); }}
          onChanged={onChanged} onOpenClient={onOpenClient} />
      ))}
      {session.status !== 'cancelled' && (
        <button className="hub__add" onClick={() => { haptic.tap(); setAdding(true); }}>{t('co.hub.addPlayer')}</button>
      )}
      {paying && (
        <LessonPaySheet bookingId={paying.bookingId} name={paying.name}
          onClose={() => { setPaying(null); onChanged(); }} onDone={onChanged} />
      )}
      {editing && (
        <EditPaymentSheet bookingId={editing.bookingId} name={editing.name}
          onClose={() => { setEditing(null); onChanged(); }} onDone={onChanged} />
      )}
      {adding && <AddClientSheet session={session} onClose={() => setAdding(false)} onDone={onChanged} />}
    </div>
  );
}

function HubPlayer({ session, booking: b, named, onPay, onEdit, onChanged, onOpenClient }: {
  session: Session; booking: Booking; named: boolean;
  onPay: () => void; onEdit: () => void; onChanged: () => void; onOpenClient?: (id: string, name?: string) => void;
}) {
  const t = useT();
  // Shown at once; the day's next fetch confirms it.
  const [status, setStatus] = useState(b.status);
  useEffect(() => setStatus(b.status), [b.status]);
  const [busy, setBusy] = useState(false);
  const pay = payLine(b);
  const [balance, setBalance] = useState<{ credits: number; payerName: string | null } | null>(null);
  useEffect(() => {
    if (pay.kind !== 'balance') return;
    let on = true;
    api<{ credits: number; payerName: string | null }>(`/api/coach/bookings/${b.bookingId}/pay-info`)
      .then((r) => { if (on) setBalance(r); }).catch(() => {});
    return () => { on = false; };
  }, [b.bookingId, pay.kind, status]);
  const href = messageHref(b.username, b.phone);

  async function mark(pressed: 'attended' | 'no_show') {
    const next = attendanceChange(status, pressed);
    if (!next || busy) return;
    if (next === 'no_show' && !(await confirm(t('co.today.noShowConfirm')))) return;
    setBusy(true);
    try {
      await api(`/api/coach/bookings/${b.bookingId}/attendance`, { method: 'POST', body: JSON.stringify({ status: next }) });
      setStatus(next);
      haptic.success();
      onChanged();
    } catch (err) {
      haptic.error();
      await alert(t('co.failed', { message: (err as Error).message }));
    } finally { setBusy(false); }
  }

  return (
    <div className="hub__p">
      {named && <div className="hub__name">{b.name}</div>}
      {attendanceOpen(session, { status, lateCancel: b.lateCancel }, Date.now()) && (
        <div className="hub__att" role="group" aria-label={t('co.hub.attendance', { name: b.name })}>
          <button className="hub__chip hub__chip--came" aria-pressed={status === 'attended'} disabled={busy}
            onClick={() => void mark('attended')}>{t('co.hub.came')}</button>
          <button className="hub__chip hub__chip--no" aria-pressed={status === 'no_show'} disabled={busy}
            onClick={() => void mark('no_show')}>{t('co.hub.noShow')}</button>
        </div>
      )}
      <div className="hub__pay">
        {pay.kind === 'collect' ? (
          <>
            <span className="hub__owed">{b.amountDue == null ? t('co.hub.cash') : t('co.today.toCollect', { amount: Math.round(pay.owed) })}</span>
            <button className="act act--cash" onClick={onPay}>{t('co.today.gotDots')}</button>
          </>
        ) : pay.kind === 'paid' ? (
          // «Изменить оплату» (components/LessonPayment.tsx): money received can be changed or undone.
          b.paymentMode === 'cash'
            ? <button className="hub__paid hub__paid--edit" onClick={onEdit} aria-label={t('lp.edit')}>✓ {t('co.today.paid')} ›</button>
            : <span className="hub__paid">✓ {t('co.today.paid')}</span>
        ) : (
          <span className="hub__bal">
            {balance === null ? t('co.today.credit')
              : t(balance.payerName ? 'co.hub.balanceOf' : 'co.hub.balance', {
                n: fmtCr(Number(balance.credits)), credits: t.word('book.credit', Number(balance.credits)),
                name: balance.payerName ?? '',
              })}
          </span>
        )}
      </div>
      {b.workingOn && <FocusNote text={b.workingOn} />}
      <div className="hub__quick">
        {/* A call is a plain link, as on the client card; Telegram opens t.me itself. */}
        {href?.startsWith('tel:') ? <a className="act hub__link" href={href}>{t('co.hub.call')}</a>
          : href && <button className="act" onClick={() => { haptic.tap(); openExternal(href); }}>{t('co.hub.write')}</button>}
        {onOpenClient && (
          <button className="act" onClick={() => { haptic.tap(); onOpenClient(b.clientId, b.name); }}>{t('co.hub.profile')}</button>
        )}
      </div>
    </div>
  );
}

/** Two letters for a face: first and last word, or the first two letters of one. */
function initials(name: string) {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const pick = words.length > 1 ? words[0][0] + words[words.length - 1][0] : words[0].slice(0, 2);
  return pick.toUpperCase();
}

/** Still to pay in cash on a lesson: its cash part less what was received. */
const owedOf = (b: Booking) => Math.max(0, Number(b.amountDue ?? 0) - Number(b.received ?? 0));
/** Euros: less than this is rounding, not a debt (as on the server, credits.ts). */
/** Credits as written: at most two decimals, a comma in Russian. */
const fmtCr = (n: number) => {
  const v = String(Math.round(n * 100) / 100);
  return getLang() === 'ru' ? v.replace('.', ',') : v;
};

/** "in 40 min", "in 2 h 10". Only ever used for something later today. */
function countdown(mins: number) {
  const tr = (key: Parameters<typeof translate>[1], vars?: Record<string, string | number>) =>
    translate(getLang(), key, vars);
  if (mins < 60) return tr('co.countdown.min', { n: mins });
  const h = Math.floor(mins / 60), m = mins % 60;
  return m ? tr('co.countdown.hMin', { h, m }) : tr('co.countdown.h', { h });
}

/**
 * Clients waiting to be given their sessions.
 *
 * Sits above the schedule because it is money in your pocket that the
 * client cannot yet use, and every day it sits there is a day they might
 * have booked and didn't. Absent entirely when there's nothing pending.
 *
 * The amount is editable at confirmation: what someone actually hands
 * over isn't always the list price, and recording the real figure is what
 * keeps the earnings numbers honest.
 */
function PackageRequests({ onChanged }: { onChanged: () => void }) {
  const t = useT();
  const [requests, setRequests] = useState<any[]>([]);
  const [editing, setEditing] = useState<any | null>(null);
  const [credits, setCredits] = useState('');
  const [paid, setPaid] = useState('');
  const [method, setMethod] = useMethod();

  useEffect(() => {
    api<any[]>('/api/coach/package-requests').then(setRequests).catch(() => setRequests([]));
  }, []);

  if (requests.length === 0) return null;

  function open(r: any) {
    setEditing(r);
    setCredits(String(r.credits_granted));
    setPaid(String(r.amount_paid));
  }

  async function confirmIt() {
    try {
      const res = await api<{ balance: number }>(
        `/api/coach/package-requests/${editing.id}/confirm`,
        { method: 'POST', body: JSON.stringify({
            credits: Number(credits), amount_paid: Number(paid), method }) });
      rememberMethod(method);
      haptic.success();
      await alert(t('co.pkg.granted', {
        name: editing.client_name, n: res.balance,
        sessions: t.word('co.today.sessions', res.balance),
      }));
      setEditing(null);
      setRequests(requests.filter((r) => r.id !== editing.id));
      onChanged();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.failed', { message: err.message }));
    }
  }

  async function decline(r: any) {
    if (!(await confirm(t('co.pkg.declineConfirm')))) return;
    await api(`/api/coach/package-requests/${r.id}/decline`, { method: 'POST', body: JSON.stringify({}) });
    haptic.success();
    setRequests(requests.filter((x) => x.id !== r.id));
  }

  if (editing) {
    return createPortal(
      <div className="sheet">
        <div className="sheet__panel">
          <div className="sheet__title">{editing.client_name}</div>
          <div className="card__when">{t('co.pkg.onlyWhenPaid')}</div>

          <label className="field" style={{ marginTop: 12 }}>
            <span className="field__label">{t('co.pkg.sessionsLabel')}</span>
            <span className="field__input">
              <input type="number" inputMode="decimal" value={credits}
                onChange={(e) => setCredits(e.target.value)} />
            </span>
          </label>
          <label className="field">
            <span className="field__label">{t('co.pkg.received')}</span>
            <span className="field__input">
              <input type="number" inputMode="decimal" value={paid}
                onChange={(e) => setPaid(e.target.value)} />
              <em>€</em>
            </span>
          </label>

          <div className="card__when" style={{ marginTop: 4 }}>
            {Number(credits) > 0
              ? t('co.pkg.perSession', { amount: (Number(paid) / Number(credits)).toFixed(0) })
              : ' '}
          </div>
          <MethodPicker value={method} onChange={setMethod} />

          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button className="paybar__opt" data-back style={{ flex: 1 }}
              onClick={() => setEditing(null)}>{t('co.pkg.back')}</button>
            <button className="paybar__opt"
              style={{ flex: 2, background: 'var(--accent)', color: 'var(--accent-text)', borderColor: 'var(--accent)' }}
              onClick={confirmIt}>{t('pm.with', { action: t('co.pkg.confirmTitle'), method: methodLabel(t, method) })}</button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  return (
    <div className="inbox">
      <div className="inbox__title">
        <span className="inbox__dot" />
        {t('co.pkg.waiting', {
          n: requests.length, packages: t.word('co.pkg.packages', requests.length),
        })}
      </div>
      {requests.map((r) => (
        <div className="inbox__row" key={r.id}>
          <span className="face face--sm">{initials(r.client_name)}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{r.client_name}</div>
            <div className="card__when">
              {r.package_name ?? t('co.pkg.sessions', {
                n: r.credits_granted, sessions: t.word('co.today.sessions', r.credits_granted),
              })} · {fmtMoney(r.amount_paid)}
            </div>
          </div>
          <button className="inbox__ghost" onClick={() => decline(r)}>{t('co.pkg.decline')}</button>
          <button className="inbox__go" onClick={() => open(r)}>{t('co.pkg.confirm')}</button>
        </div>
      ))}
    </div>
  );
}

/**
 * The coach's private note. Never leaves this screen — "losing
 * confidence on the backhand" is an observation, not feedback.
 */
function CoachNote({ booking }: { booking: Booking }) {
  const t = useT();
  const [note, setNote] = useState(booking.coachNote ?? '');
  const [saved, setSaved] = useState(booking.coachNote ?? '');
  const [open, setOpen] = useState(Boolean(booking.coachNote));

  if (open === false) {
    return (
      <button className="sess__link" onClick={() => setOpen(true)}>{t('co.today.addPrivateNote')}</button>
    );
  }

  return (
    <div className="sess__note">
      <input className="notearea" style={{ minHeight: 0 }}
        placeholder={t('co.today.privateNote')}
        value={note} onChange={(e) => setNote(e.target.value)} />
      {note !== saved && (
        <button className="savebtn" onClick={async () => {
          await api(`/api/coach/bookings/${booking.bookingId}/note`, {
            method: 'PUT', body: JSON.stringify({ note }),
          });
          setSaved(note);
          haptic.success();
        }}>{t('co.today.saveNote')}</button>
      )}
    </div>
  );
}

/**
 * Rating a session.
 *
 * Every skill opens at 0 — not trained today — and the coach drags up the
 * ones they worked on (909155b). Each skill shows its last score as
 * «в прошлый раз 6»; one tap takes it, so a skill that stayed where it
 * was costs a tap, not a drag. Anything slower than about twenty seconds
 * and rating stops happening after a fortnight, which is how every
 * abandoned system in this category died.
 *
 * 1-10 because this is a judgement, not a measurement. A percentage
 * implies a precision that does not exist.
 *
 * Opened from the Today card during the lesson, `live` puts the lesson's
 * header on top (LiveHeader).
 */
/** Also opened from a client's past sessions, which know only these three things. */
export function RatingSheet({ booking, live, hub, onClose, onDone }: {
  booking: Pick<Booking, 'bookingId' | 'clientId' | 'name'>;
  live?: { startsAt: string; endsAt: string; who: string };
  /** «Панель занятия» under the header: attendance, payment, quick buttons. */
  hub?: ReactNode;
  onClose: () => void; onDone: () => void;
}) {
  const t = useT();
  const [axes, setAxes] = useState<any[] | null>(null);
  const [scores, setScores] = useState<Record<string, number>>({});
  /** Their last score per skill, for «в прошлый раз N». */
  const [last, setLast] = useState<Record<string, number>>({});
  /** A copy kept in this browser came back: Telegram closed before it was sent. */
  const [restored, setRestored] = useState(false);
  const [workingOnDraft, setWorkingOnDraft] = useState('');
  const [busy, setBusy] = useState(false);
  // What the session was, captured while it is fresh. Feeds the player's
  // session history and the AI read of the notes.
  const [focus, setFocus] = useState<string[]>([]);
  const [customFocus, setCustomFocus] = useState('');
  const [drills, setDrills] = useState('');
  const [shared, setShared] = useState('');
  const [homework, setHomework] = useState('');
  /** Which band word the coach tapped to read what it means. */
  const [openGuide, setOpenGuide] = useState<string | null>(null);
  // The draft: kept on the booking while the coach types, so nothing is
  // lost if the app closes mid-lesson. The player never sees it.
  const [loaded, setLoaded] = useState(false);
  const [draftState, setDraftState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [publishAt, setPublishAt] = useState<string | null>(null);
  const [endsAt, setEndsAt] = useState<string | null>(null);
  const lastSent = useRef('');
  const pending = useRef<string | null>(null);
  const inFlight = useRef<Promise<void>>(Promise.resolve());
  const closed = useRef(false);

  /** Rated above zero, in the coach's own axis order, then anything typed. */
  const ratedAxes = (axes ?? []).filter((a) => (scores[a.id] ?? 0) > 0);
  const rated = ratedAxes.map((a) => axisName(a));
  /**
   * A focus saved before the skill had a name in this language is the
   * same skill, not a second one: match on either name before treating
   * something as the coach's own extra.
   */
  const bothNames = new Set(ratedAxes.flatMap((a) => [a.name, a.name_ru].filter(Boolean)));
  const focusFromScores = [...rated, ...focus.filter((f) => !bothNames.has(f) && !rated.includes(f))];
  const addExtra = (text: string) => {
    if (!focus.includes(text) && !rated.includes(text)) setFocus([...focus, text].slice(0, 6));
    setCustomFocus('');
  };

  useEffect(() => {
    Promise.all([
      // The lesson's own sport's skills (tennis or padel), by its booking.
      api<any[]>(`/api/rating-axes?booking=${booking.bookingId}`),
      api<any[]>(`/api/ratings/${booking.bookingId}`),
      api<any[]>(`/api/coach/clients/${booking.clientId}/last-scores`),
      api<any>(`/api/coach/clients/${booking.clientId}`),
      api<any>(`/api/coach/bookings/${booking.bookingId}/detail`).catch(() => null),
      api<any>(`/api/coach/bookings/${booking.bookingId}/rating-draft`).catch(() => null),
    ])
      .then(([ax, existing, last, detail, session, saved]) => {
        if (session) {
          setFocus(session.focus ?? []); setDrills(session.drills ?? '');
          setShared(session.shared_note ?? ''); setHomework(session.homework ?? '');
        }
        setWorkingOnDraft(detail?.client?.working_on ?? '');
        setAxes(ax);
        setLast(lastScores(last));
        // Every skill starts at zero — not trained — and the coach drags
        // up the ones they worked on. Anything already saved for this
        // session comes back as it was.
        const start: Record<string, number> = {};
        for (const a of ax) start[a.id] = 0;
        for (const e of existing) {
          if (e.rated_by === 'coach') start[e.axis_id] = Number(e.score);
        }
        // A draft from earlier — typed during the lesson, or before the
        // app closed — is what the sheet was showing, so it wins. Newer
        // still: a copy in this browser of changes that never reached the
        // server (Telegram killed offline). Sent as soon as it is back.
        let d = saved?.draft;
        const kept = readLocal(browserStore(), rateKey(booking.bookingId), Date.now());
        const local = restoreFrom(kept, saved?.draftAt ?? null);
        // Older than what the server holds (typed on another device since): not needed.
        if (kept && !local) dropLocal(browserStore(), rateKey(booking.bookingId));
        if (local) {
          try {
            const l = JSON.parse(local);
            if (l && typeof l.scores === 'object') { d = l; lastSent.current = '-'; setRestored(true); }
          } catch { /* unreadable: the server's draft stands */ }
        }
        if (d) {
          setScores({ ...start, ...d.scores });
          setFocus(d.focus ?? []); setDrills(d.drills ?? '');
          if (d.shared !== undefined) setShared(d.shared);
          if (d.homework !== undefined) setHomework(d.homework);
          setWorkingOnDraft(d.workingOn ?? '');
          setDraftState('saved');
        } else {
          setScores(start);
        }
        setPublishAt(saved?.publishAt ?? null);
        setEndsAt(saved?.endsAt ?? null);
        setLoaded(true);
      })
      .catch(() => setAxes([]));
  }, [booking.bookingId]);

  /**
   * Everything on the sheet. The "working on" line is saved with the
   * rating, because this is the moment the coach knows what needs work.
   * Shared note and homework are left out while those fields are hidden,
   * so a save here cannot wipe a note written before they went away.
   */
  const draft = {
    scores, focus, focusAll: focusFromScores, drills, workingOn: workingOnDraft,
    ...(PLAYER_SEES_SHARED_NOTES ? { shared, homework } : {}),
  };
  const draftJson = loaded ? JSON.stringify(draft) : '';

  /** Sends whatever is waiting. One at a time, so they arrive in order. */
  const flush = useRef((_keepalive?: boolean) => Promise.resolve());
  flush.current = (keepalive = false) => {
    inFlight.current = inFlight.current.then(async () => {
      const body = pending.current;
      if (!body || closed.current) return;
      pending.current = null;
      try {
        const r = await api<{ publishAt: string | null }>(`/api/coach/bookings/${booking.bookingId}/rating-draft`, {
          method: 'PUT', body: `{"draft":${body}}`, keepalive,
        });
        lastSent.current = body;
        // On the server now: the browser's copy is no longer needed.
        if (readLocal(browserStore(), rateKey(booking.bookingId), Date.now())?.json === body) dropLocal(browserStore(), rateKey(booking.bookingId));
        setDraftState('saved');
        setPublishAt(r.publishAt ?? null);
      } catch {
        // Tried again with the next change, or when the sheet closes.
        if (pending.current === null) pending.current = body;
        setDraftState('idle');
      }
    });
    return inFlight.current;
  };

  // Kept a moment after the last change, not on every letter.
  useEffect(() => {
    if (!loaded) return;
    if (!lastSent.current) { lastSent.current = draftJson; return; }
    if (draftJson === lastSent.current) { pending.current = null; return; }
    pending.current = draftJson;
    keepLocal(browserStore(), rateKey(booking.bookingId), draftJson, Date.now());
    setDraftState('saving');
    const timer = setTimeout(() => void flush.current(), 600);
    return () => clearTimeout(timer);
  }, [draftJson, loaded]);

  // Telegram closed or sent to the background, or the sheet closed: sent at once.
  useEffect(() => {
    const away = () => { if (document.visibilityState === 'hidden') void flush.current(true); };
    const hide = () => void flush.current(true);
    document.addEventListener('visibilitychange', away);
    window.addEventListener('pagehide', hide);
    return () => {
      document.removeEventListener('visibilitychange', away);
      window.removeEventListener('pagehide', hide);
      void flush.current(true);
    };
  }, []);

  async function later() {
    await flush.current();
    onDone();
    onClose();
  }

  /** After the lesson: shown to the player now. During it: at its end time. */
  async function save() {
    setBusy(true);
    // No draft may land after the save and bring back a draft.
    closed.current = true;
    pending.current = null;
    try {
      await inFlight.current;
      await api(`/api/coach/bookings/${booking.bookingId}/rating`, {
        method: 'POST', body: JSON.stringify({ draft }),
      });
      dropLocal(browserStore(), rateKey(booking.bookingId));
      haptic.success();
      onDone();
      onClose();
    } catch (err: any) {
      closed.current = false;
      haptic.error();
      await alert(t('co.rate.couldNotSave', { message: err.message }));
    } finally { setBusy(false); }
  }

  const first = booking.name.split(' ')[0];
  const lessonOn = endsAt !== null && new Date(endsAt).getTime() > Date.now();

  return createPortal(
    <div className="sheet">
      <div className="sheet__panel sheet--rate">
        {live && <LiveHeader startsAt={live.startsAt} endsAt={live.endsAt} who={live.who} />}
        {live && hub}
        <div className="sheet__title">{t('co.rate.title', { name: booking.name })}</div>
        {axes !== null && axes.length > 0 && (
          <div className="card__when">
            {t('co.rate.ratedCount', { n: rated.length, total: axes.length })}
          </div>
        )}

        {axes === null ? (
          <div className="skeleton" style={{ marginTop: 12 }} />
        ) : axes.length === 0 ? (
          <div className="card__when" style={{ marginTop: 12 }}>
            {t('co.rate.noAxes')}
          </div>
        ) : (
          <>
            <div className="card__when">{t('co.rate.dragHint')}</div>
            <div className="ratelines">
              {axes.map((a) => {
                const v = scores[a.id] ?? 0;
                // What this band means, in the words the coach wrote in Setup.
                const guide = guideFor(a, v, getLang());
                const open = openGuide === a.id;
                return (
                  <div className={`rateline${v === 0 ? ' rateline--off' : ''}`} key={a.id}>
                    <div className="rateline__head">
                      {/* The coach's own axis name, exactly as Setup has it. */}
                      <span className="rateline__name">{axisName(a)}</span>
                      {guide ? (
                        <button className="rateline__word rateline__word--tap" aria-expanded={open}
                          onClick={() => { haptic.tap(); setOpenGuide(open ? null : a.id); }}>
                          {t(ratingWord(v))}
                        </button>
                      ) : (
                        <span className="rateline__word">{t(ratingWord(v))}</span>
                      )}
                      <span className="rateline__num">{v}</span>
                    </div>
                    <input type="range" min={0} max={10} step={1}
                      value={v} aria-label={axisName(a)} aria-valuetext={`${v} — ${t(ratingWord(v))}`}
                      onChange={(e) => setScores({ ...scores, [a.id]: Number(e.target.value) })} />
                    {last[a.id] !== undefined && (
                      <button className="rateline__last" aria-pressed={v === last[a.id]}
                        aria-label={t('co.rate.lastTimeSet', { n: last[a.id] })}
                        onClick={() => { haptic.tap(); setScores(takeLast(scores, a.id, last[a.id])); }}>
                        {t('co.rate.lastTime', { n: last[a.id] })}
                      </button>
                    )}
                    {open && guide && <div className="rateline__guide">{guide}</div>}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* What it was about is no longer typed twice: it is the skills
            that got a score, plus anything outside the axes. */}
        <div className="ratefocus">
          <span className="mf__label"><VisLabel text={t('co.rate.aboutLabel')} who="player" /></span>
          <div className="pp-tagpick">
            {focusFromScores.map((name) => (
              <span key={name} className="chip chip--on">{name}</span>
            ))}
            <input id="rate-focus-other" className="pp-tagpick__add" placeholder={t('co.rate.other')}
              value={customFocus}
              onChange={(e) => setCustomFocus(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && customFocus.trim()) {
                  e.preventDefault(); addExtra(customFocus.trim());
                }
              }}
              onBlur={() => { if (customFocus.trim()) addExtra(customFocus.trim()); }} />
          </div>
          <div className="card__when">{t('co.rate.aboutHint')}</div>
        </div>
        <label className="mf" style={{ marginTop: 12 }}>
          <span className="mf__label"><VisLabel text={t('co.rate.drillsLabel')} who="player" /></span>
          <GrowArea id="rate-drills" className="notearea" rows={1} value={drills}
            placeholder={t('co.rate.drills')}
            onChange={(e) => setDrills(e.target.value)} />
          <DrillSuggest value={drills} onChange={setDrills} />
        </label>
        {PLAYER_SEES_SHARED_NOTES && (
          <>
            <label className="mf" style={{ marginTop: 12 }}>
              <span className="mf__label">
                <VisLabel text={t('co.rate.sharedLabel', { name: booking.name.split(' ')[0] })} who="player" />
              </span>
              <GrowArea id="rate-shared" className="notearea" rows={2} value={shared}
                placeholder={t('co.rate.sharedNote')}
                onChange={(e) => setShared(e.target.value)} />
            </label>
            <label className="mf" style={{ marginTop: 12 }}>
              <span className="mf__label"><VisLabel text={t('co.rate.homeworkLabel')} who="player" /></span>
              {/* Wraps and grows downwards: a one-line field ran long text off sideways. */}
              <GrowArea id="rate-homework" className="notearea" rows={1} value={homework} placeholder={t('co.rate.homework')}
                onChange={(e) => setHomework(e.target.value)} />
            </label>
          </>
        )}

        <div className="sheet__label" style={{ marginTop: 14 }}>
          <VisLabel text={t('co.rate.workingOnLabel')} who="player" />
        </div>
        <GrowArea id="rate-working-on" className="notearea" rows={1}
          placeholder={t('co.rate.workingOn')}
          value={workingOnDraft}
          onChange={(e) => setWorkingOnDraft(e.target.value)} />
        <div className="card__when" style={{ marginTop: 4 }}>
          {t('co.rate.clearWhenDone')}
        </div>

        {(draftState !== 'idle' || publishAt || lessonOn || restored) && (
          <div className="rate-draft" role="status">
            {restored && <div>{t('co.rate.restored')}</div>}
            {draftState === 'saving' && <div>{t('co.rate.draftSaving')}</div>}
            {draftState === 'saved' && !publishAt && <div>{t('co.rate.draftSaved', { name: first })}</div>}
            {publishAt ? <div>{t('co.rate.willShow', { name: first, time: fmtTime(publishAt) })}</div>
              : lessonOn && <div>{t('co.rate.duringLesson', { name: first, time: fmtTime(endsAt!) })}</div>}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button className="paybar__opt" data-back style={{ flex: 1 }} onClick={later}>{t('co.rate.later')}</button>
          <button className="paybar__opt"
            style={{ flex: 2, background: 'var(--accent)', color: 'var(--accent-text)', borderColor: 'var(--accent)' }}
            disabled={busy || !axes?.length} onClick={save}>
            {busy ? t('co.rate.saving') : t('co.rate.save')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Putting a second person into a session.
 *
 * Two players on a Saturday are one court and one hour, but two people
 * to rate and two sets of notes. This gives them separate bookings on
 * the same session, so everything per-person keeps working.
 *
 * Works on past sessions — which is when the coach remembers, having
 * just rated the first player.
 *
 * Never on a tap: picking someone shows who exactly (name and @username,
 * large) and what it costs, and only «Добавить» adds — two clients with
 * nearly the same name is how the wrong one got in.
 *
 * With `replacing`, «Заменить»: the same picker, and the one picked takes
 * that person's seat at the same price (server: booking/remove.ts). The
 * one leaving goes as with «Убрать»; added under ten minutes ago, they
 * probably never knew, so the message is off by default then.
 */
function AddClientSheet({ session, replacing: replacingSeat, reassigning, onClose, onDone }: {
  session: Session; replacing?: Booking;
  /** «Заменить» after the session with saved work: the booking moves (server: reassignParticipant). */
  reassigning?: { booking: Booking; moves: SeatMoves; solo?: boolean };
  onClose: () => void; onDone: () => void;
}) {
  const replacing = replacingSeat ?? reassigning?.booking;
  const t = useT();
  const [clients, setClients] = useState<any[] | null>(null);
  const [search, setSearch] = useState('');
  const [payWith, setPayWith] = useState<'credits' | 'cash' | 'covered'>('credits');
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState<any | null>(null);
  const recent = addedJustNow(replacing?.bookedAt);
  const [silent, setSilent] = useState(recent);
  // The seat being taken over: its price and share of credits.
  const [seat, setSeat] = useState<{ credits: number; price: number; covered: boolean } | null>(null);
  useEffect(() => {
    if (!replacingSeat) return;
    api<{ credits: number; price: number; covered: boolean }>(`/api/coach/bookings/${replacingSeat.bookingId}/replace-preview`)
      .then(setSeat).catch(() => setSeat(null));
  }, [replacingSeat?.bookingId]);
  // Half the solo price, rounded. The coach can overtype it.
  const solo = Number(session.bookings[0]?.amountDue ?? 0);
  const people = session.bookings.length + (replacing ? 0 : 1);
  const [priceEach, setPriceEach] = useState(String(Math.round(solo / people) || ''));
  const creditsEach = replacing ? Number(seat?.credits ?? 1 / people) : 1 / people;
  const seatPrice = replacing ? Number(seat?.price ?? 0) : Number(priceEach);
  const covered = payWith === 'covered' || (!!replacing && !!seat?.covered);

  useEffect(() => {
    api<{ clients: any[] }>('/api/coach/insights')
      .then((r) => setClients(r.clients)).catch(() => setClients([]));
  }, []);

  const already = new Set(session.bookings.map((b) => b.clientId));
  const shown = (clients ?? [])
    .filter((c) => !already.has(c.id))
    .filter((c) => !search || c.name.toLowerCase().includes(search.toLowerCase()))
    .slice(0, 12);

  /** «0,5 кредита», «35 €», or already paid. */
  const priceText = payWith === 'credits'
    ? `${fmtCr(creditsEach)} ${t.word('book.credit', creditsEach)}`
    : fmtMoney(seatPrice);
  const who = (c: any) => c.username ? `${c.name} (@${c.username})` : c.name;

  async function add(c: any) {
    setBusy(true);
    try {
      const res = await api<{ past: boolean }>(`/api/coach/sessions/${session.id}/add-client`, {
        method: 'POST',
        body: JSON.stringify({ client_id: c.id, payment_mode: payWith,
          price_each: payWith === 'cash' ? Number(priceEach) : undefined }),
      });
      haptic.success();
      await alert(res.past
        ? t('co.rate.added', { name: c.name })
        : t('co.rate.addedClient', { name: c.name }));
      onDone();
      onClose();
    } catch (err: any) {
      haptic.error();
      await alert(err.message === 'insufficient_credits'
        ? t('co.rate.noCredits', { name: c.name })
        : err.message === 'already_in_session'
          ? t('co.rate.alreadyIn')
          : t('co.rate.couldNotAdd', { message: err.message }));
    } finally { setBusy(false); }
  }

  // What moves, for «Перенести занятие … с A на B: оценки, заметки, оплата 35 €».
  const whenText = `${new Date(session.starts_at).toLocaleDateString(getLang() === 'ru' ? 'ru-RU' : 'en-GB', { day: 'numeric', month: 'long' })}, ${fmtTime(session.starts_at)}`;
  const movesText = reassigning ? (() => {
    const m = reassigning.moves;
    const pay = [m.cash > 0 ? fmtMoney(m.cash) : '', m.credits > 0 ? `${fmtCr(m.credits)} ${t.word('book.credit', m.credits)}` : '']
      .filter(Boolean).join(' + ');
    return [m.ratings ? t('co.rs.ratings') : '', m.notes ? t('co.rs.notes') : '', pay ? t('co.rs.payment', { amount: pay }) : '']
      .filter(Boolean).join(', ');
  })() : '';

  async function reassign(c: any) {
    if (!reassigning) return;
    const old = reassigning.booking;
    setBusy(true);
    try {
      const r = await api<{ refunded: number; charged: number; notified: boolean }>(`/api/coach/bookings/${old.bookingId}/reassign`, {
        method: 'POST', body: JSON.stringify({ client_id: c.id, notify: !silent }),
      });
      haptic.success();
      onDone();
      onClose();
      await alert(t('co.rs.done', { new: c.name, when: whenText })
        + (r.refunded > 0 ? t('co.rp.doneCredits', { old: old.name, n: fmtCr(r.refunded), credits: t.word('book.credit', r.refunded) }) : '')
        + (r.charged > 0 ? t('co.rs.charged', { new: c.name, n: fmtCr(r.charged), credits: t.word('book.credit', r.charged) }) : '')
        + (r.notified && old.botStarted ? t('co.rm.doneTold') : t('co.rm.doneSilent')));
    } catch (err: any) {
      haptic.error();
      await alert(err.message === 'already_in_session' ? t('co.rate.alreadyIn') : removeErrorText(t, old.name, err.message));
    } finally { setBusy(false); }
  }

  async function replace(c: any) {
    if (!replacing) return;
    setBusy(true);
    try {
      const r = await api<{ refunded: number; notified: boolean }>(`/api/coach/bookings/${replacing.bookingId}/replace`, {
        method: 'POST',
        body: JSON.stringify({ client_id: c.id, payment_mode: payWith, notify: !silent }),
      });
      haptic.success();
      onDone();
      onClose();
      await alert(t('co.rp.done', { new: c.name, old: replacing.name })
        + (r.refunded > 0 ? t('co.rp.doneCredits', { old: replacing.name, n: fmtCr(r.refunded), credits: t.word('book.credit', r.refunded) }) : '')
        + (r.notified && replacing.botStarted ? t('co.rm.doneTold') : t('co.rm.doneSilent')));
    } catch (err: any) {
      haptic.error();
      await alert(err.message === 'already_in_session' ? t('co.rate.alreadyIn') : removeErrorText(t, replacing.name, err.message));
    } finally { setBusy(false); }
  }

  return createPortal(
    <div className="sheet" onClick={onClose}>
      <div className="sheet__panel" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__title">
          {reassigning ? t('co.rs.title', { name: reassigning.booking.name })
            : replacing ? t('co.rp.title', { name: replacing.name }) : t('co.add.title', { time: fmtTime(session.starts_at) })}
        </div>

        {picked ? (
          // Who exactly, and for what: nothing happens until «Добавить» / «Заменить».
          <div className="addc">
            <div className="addc__who">{picked.name}</div>
            {picked.username && <div className="addc__user">@{picked.username}</div>}
            <p className="addc__q">
              {reassigning
                ? (movesText ? t('co.rs.confirm', { when: whenText, old: reassigning.booking.name, new: picked.name, items: movesText })
                  : t('co.rs.confirmPlain', { when: whenText, old: reassigning.booking.name, new: picked.name }))
                : replacing
                ? t('co.rp.confirm', { old: replacing.name, new: picked.name })
                : covered
                  ? t('co.add.confirmCovered', { who: who(picked), time: fmtTime(session.starts_at) })
                  : t('co.add.confirm', { who: who(picked), time: fmtTime(session.starts_at), price: priceText })}
            </p>
            {replacing && (
              <>
                <p className="addc__line">{t('co.rp.oldLine', { old: replacing.name })}</p>
                {!reassigning && <p className="addc__line">{covered ? t('co.rp.sameCovered') : t('co.rp.samePrice', { price: priceText })}</p>}
                <div className="field">
                  <span className="field__label field__label--strong">
                    {t('co.rm.silent')}
                    <small>{recent ? t('co.rp.recent', { name: replacing.name }) : t('co.rm.silentHint')}</small>
                  </span>
                  <button type="button" role="switch" aria-checked={silent} aria-label={t('co.rm.silent')}
                    className="switch" onClick={() => { haptic.tap(); setSilent(!silent); }}>
                    <span className="switch__knob" />
                  </button>
                </div>
              </>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button className="paybar__opt" style={{ flex: 1 }} onClick={() => { haptic.tap(); setPicked(null); }}>
                {t('co.offer.cancel')}
              </button>
              <button className="paybar__opt addc__go" style={{ flex: 2 }} disabled={busy}
                onClick={() => (reassigning ? reassign(picked) : replacing ? replace(picked) : add(picked))}>
                {reassigning ? t('co.rs.do') : replacing ? t('co.rp.do') : t('co.add.do')}
              </button>
            </div>
          </div>
        ) : (<>
        {reassigning ? (
          <div className="card__when" style={{ marginTop: 8 }}>
            {!reassigning.solo ? t('co.rs.hint', { name: reassigning.booking.name, items: movesText })
              : movesText ? t('co.rs.hintSolo', { items: movesText }) : t('co.rs.hintPlain')}
          </div>
        ) : (<>
        <div className="paybar" style={{ padding: '10px 0 4px' }}>
          <button className="paybar__opt" aria-pressed={payWith === 'credits'}
            onClick={() => { haptic.tap(); setPayWith('credits'); }}>{t('co.rate.useCredits')}</button>
          <button className="paybar__opt" aria-pressed={payWith === 'cash'}
            onClick={() => { haptic.tap(); setPayWith('cash'); }}>{t('co.rate.cash')}</button>
          <button className="paybar__opt" aria-pressed={payWith === 'covered'}
            onClick={() => { haptic.tap(); setPayWith('covered'); }}>{t('co.rate.alreadyPaid')}</button>
        </div>
        {payWith === 'covered' && (
          <div className="card__when" style={{ marginTop: 8 }}>
            {t('co.rate.paidInFull')}
          </div>
        )}

        {/* Replacing: the seat's own price, nobody else's changes. */}
        {replacing && payWith !== 'covered' && seat && (
          <div className="card__when" style={{ marginTop: 8 }}>
            {seat.covered ? t('co.rp.sameCovered') : t('co.rp.samePrice', { price: priceText })}
          </div>
        )}
        {!replacing && payWith === 'cash' && (
          <label className="field" style={{ marginTop: 8 }}>
            <span className="field__label">{t('co.rate.eachPays')}</span>
            <span className="field__input">
              <input type="number" inputMode="decimal" value={priceEach}
                onChange={(e) => setPriceEach(e.target.value)} />
              <em>€</em>
            </span>
          </label>
        )}
        {!replacing && payWith === 'credits' && (
          <div className="card__when" style={{ marginTop: 8 }}>
            {t('co.add.eachUses', { n: (1 / people).toFixed(1).replace('.0', ''), credits: t.word('book.credit', Number((1 / people).toFixed(1))) })}
          </div>
        )}
        </>)}

        <input className="paybar__opt" style={{ width: '100%', textAlign: 'left', marginTop: 8 }}
          placeholder={t('co.rate.searchClient')} value={search} autoFocus
          onChange={(e) => setSearch(e.target.value)} />

        <div style={{ maxHeight: '40vh', overflowY: 'auto', marginTop: 8 }}>
          {clients === null ? <div className="skeleton" /> :
           shown.length === 0 ? <div className="card__when">{t('co.add.nobody')}</div> :
           shown.map((c) => (
            <button key={c.id} className="daysheet__row" disabled={busy}
              style={{ width: '100%', textAlign: 'left', background: 'none', border: 0, borderBottom: '1px solid var(--line-soft)' }}
              onClick={() => { haptic.tap(); setPicked(c); }}>
              <span style={{ flex: 1 }}>{c.name}</span>
              <span className="card__when">{c.credits > 0 ? t('co.add.credits', { n: c.credits }) : ''}</span>
            </button>
          ))}
        </div>

        <button className="paybar__opt" style={{ width: '100%', marginTop: 12 }}
          onClick={onClose}>{t('co.pkg.back')}</button>
        </>)}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Reviewing a match.
 *
 * Same six sliders as a training rating, so the two shapes overlay —
 * but scored as the coach saw it under pressure, not in a drill. Plus
 * two things the player will actually read: what worked, what to fix.
 *
 * It opens on whatever is already there. A review is a judgement, and a
 * coach is allowed to change their mind about one a week later — so the
 * same sheet writes the first review and edits the fifth, with the
 * sliders and both notes where the coach last left them.
 */
export function MatchReviewSheet({ match, onClose, onDone }: {
  match: any; onClose: () => void; onDone: () => void;
}) {
  const t = useT();
  const [axes, setAxes] = useState<any[] | null>(null);
  const [scores, setScores] = useState<Record<string, number>>({});
  const [worked, setWorked] = useState('');
  const [didnt, setDidnt] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      // Matches are padel (sql/046: everything before sports was).
      api<any[]>('/api/rating-axes?sport=padel'),
      api<{ match: any; ratings: any[] }>(`/api/coach/matches/${match.id}`),
    ]).then(([ax, full]) => {
      setAxes(ax);
      // Same rule as a session: nothing is rated until the coach says so,
      // and a skill the match never showed stays at zero. A skill already
      // rated opens where the coach left it.
      const start: Record<string, number> = {};
      for (const a of ax) start[a.id] = 0;
      for (const r of full.ratings) start[r.axis_id] = r.score;
      setScores(start);
      setWorked(full.match.coach_worked ?? '');
      setDidnt(full.match.coach_didnt ?? '');
    }).catch(() => setAxes([]));
  }, [match.id]);

  async function save() {
    setBusy(true);
    try {
      await api(`/api/coach/matches/${match.id}/review`, {
        method: 'PUT', body: JSON.stringify({ scores, worked, didnt }),
      });
      haptic.success();
      onDone();
      onClose();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.rate.couldNotSave', { message: err.message }));
    } finally { setBusy(false); }
  }

  return createPortal(
    <div className="sheet" onClick={onClose}>
      <div className="sheet__panel sheet--rate" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__title">
          {t('co.review.title', {
            name: match.name,
            result: match.result === 'won' ? t('co.review.won') : t('co.review.lost'),
            score: match.score ? ` ${match.score}` : '',
          })}
        </div>
        {match.opponent && <div className="card__when">{t('co.review.versus', { name: match.opponent })}</div>}
        {match.client_feeling && (
          <div className="clientnote" style={{ marginTop: 8 }}>"{match.client_feeling}"</div>
        )}
        {match.link && (
          <button className="paybar__opt" style={{ width: '100%', marginTop: 10 }}
            onClick={() => openExternal(match.link)}>{t('co.review.watch')}</button>
        )}

        {axes === null ? <div className="skeleton" style={{ marginTop: 12 }} /> : (
          <>
            <div className="card__when">{t('co.review.dragHint')}</div>
            <div className="ratelines">
              {axes.map((a) => {
                const v = scores[a.id] ?? 0;
                // Nothing is "trained" in a match: a skill at zero is one
                // the match never asked for.
                const word = v === 0 ? t('co.review.w0') : t(ratingWord(v));
                return (
                  <div className={`rateline${v === 0 ? ' rateline--off' : ''}`} key={a.id}>
                    <div className="rateline__head">
                      {/* The coach's own axis name, exactly as Setup has it. */}
                      <span className="rateline__name">{axisName(a)}</span>
                      <span className="rateline__word">{word}</span>
                      <span className="rateline__num">{v}</span>
                    </div>
                    <input type="range" min={0} max={10} step={1}
                      value={v} aria-label={axisName(a)} aria-valuetext={`${v} — ${word}`}
                      onChange={(e) => setScores({ ...scores, [a.id]: Number(e.target.value) })} />
                  </div>
                );
              })}
            </div>
          </>
        )}

        <textarea className="paybar__opt"
          style={{ width: '100%', textAlign: 'left', marginTop: 8, minHeight: 60, resize: 'vertical' }}
          placeholder={t('co.review.worked')} value={worked} onChange={(e) => setWorked(e.target.value)} />
        <textarea className="paybar__opt"
          style={{ width: '100%', textAlign: 'left', marginTop: 8, minHeight: 60, resize: 'vertical' }}
          placeholder={t('co.review.toWorkOn')} value={didnt} onChange={(e) => setDidnt(e.target.value)} />

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="paybar__opt" style={{ flex: 1 }} onClick={onClose}>{t('co.rate.later')}</button>
          <button className="paybar__opt"
            style={{ flex: 2, background: 'var(--accent)', color: 'var(--accent-text)', borderColor: 'var(--accent)' }}
            disabled={busy || !axes?.length} onClick={save}>
            {busy ? t('co.rate.saving')
              : match.reviewed_at ? t('co.review.saveChanges') : t('co.review.saveReview')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Matches waiting to be watched, shown above the day's schedule. */
function PendingMatches({ onChanged }: { onChanged: () => void }) {
  const t = useT();
  const [rows, setRows] = useState<any[]>([]);
  const [reviewing, setReviewing] = useState<any | null>(null);

  const load = () => api<any[]>('/api/coach/matches/pending').then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  if (rows.length === 0) return null;

  return (
    <>
      <div className="inbox inbox--match">
        <div className="inbox__title">
          <span className="inbox__dot" />
          {t('co.review.toReview', { n: rows.length, matches: t.word('co.review.matches', rows.length) })}
        </div>
        {rows.map((m) => (
          <div className="inbox__row" key={m.id}>
            <span className={`face face--sm ${m.result === 'won' ? 'face--won' : 'face--lost'}`}>
              {m.result === 'won' ? 'W' : 'L'}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>{m.name}</div>
              <div className="card__when">
                {m.result === 'won' ? t('co.review.won') : t('co.review.lost')}{m.score ? ` ${m.score}` : ''}
                {m.review_requested && (
                  <span style={{ color: 'var(--held)' }}> · {fmtMoney(Number(m.review_fee))}{m.review_paid_at ? t('co.review.paid') : ''}</span>
                )}
              </div>
            </div>
            {m.review_requested && !m.review_paid_at && (
              <button className="inbox__ghost" onClick={async () => {
                await api(`/api/coach/matches/${m.id}/fee-paid`, { method: 'POST', body: JSON.stringify({}) });
                haptic.success(); load();
              }}>{t('co.today.got', { amount: Number(m.review_fee) })}</button>
            )}
            <button className="inbox__go" onClick={() => setReviewing(m)}>{t('co.review.open')}</button>
          </div>
        ))}
      </div>
      {reviewing && (
        <MatchReviewSheet match={reviewing}
          onClose={() => setReviewing(null)} onDone={() => { load(); onChanged(); }} />
      )}
    </>
  );
}

/**
 * A "working on" note, two lines of it.
 *
 * These run long — a coach writes three sentences about the grip change
 * — and a day of them buries the schedule. Two lines and "…more" keeps
 * the shape of the day readable. Clamped by line rather than by
 * character count, so it never cuts a word in half in either language.
 */
function FocusNote({ text, className }: { text: string; className?: string }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <div className={className ?? 'sess__line'}>
      <span>🎯</span>
      <span style={{ minWidth: 0 }}>
        <span className={open ? undefined : 'clamp2'}>{text}</span>
        {!open && (
          <button className="sess__more" onClick={(e) => { e.stopPropagation(); setOpen(true); }}>
            {t('co.more')}
          </button>
        )}
      </span>
    </div>
  );
}

/**
 * Something is saved for this player: ratings, their note or homework, the
 * session's note for everyone, or a Save made during the lesson that shows
 * at its end.
 */
const isRated = (b: Booking, group: Session | null) =>
  (b.ratings ?? []).length > 0 || !!b.hasNote || !!b.hasHomework
  || (group ? !!group.commonNote || !!group.notesPublishAt : !!b.ratingPublishAt);

/** A pair or group player with only the note for everyone saved: no ratings, note or homework of their own. */
const onlyShared = (b: Booking, group: Session | null) =>
  !!group?.commonNote && (b.ratings ?? []).length === 0 && !b.hasNote && !b.hasHomework;

function SessionCard({ session, onChanged, onOpenClient }: { session: Session; onChanged: () => void; onOpenClient?: (id: string, name?: string) => void }) {
  const t = useT();
  const past = new Date(session.ends_at).getTime() < Date.now();
  const [cancelling, setCancelling] = useState<Booking | null>(null);
  const [moving, setMoving] = useState<Booking | null>(null);
  const [rating, setRating] = useState<Booking | null>(null);
  // Two or more people on it: one notes sheet for the whole session, opened on this player's tab.
  const [groupTab, setGroupTab] = useState<string | null>(null);
  const [paying, setPaying] = useState<Booking | null>(null);
  const [editing, setEditing] = useState<Booking | null>(null);
  const [switchFor, setSwitchFor] = useState<Booking | null>(null);
  const [adding, setAdding] = useState(false);
  // «⋯» on a pair or group player: «Убрать из занятия» shown, then its sheet.
  const [moreFor, setMoreFor] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Booking | null>(null);
  const [replacing, setReplacing] = useState<Booking | null>(null);
  const [reassigning, setReassigning] = useState<{ booking: Booking; moves: SeatMoves; solo?: boolean } | null>(null);
  // A pair or group: «Отменить занятие» for everyone, under the session's ⋯.
  const [sessMore, setSessMore] = useState(false);
  const [cancellingAll, setCancellingAll] = useState(false);

  async function act(b: Booking, path: string, body: any, confirmText?: string) {
    if (confirmText && !(await confirm(confirmText))) return;
    await api(`/api/coach/bookings/${b.bookingId}/${path}`, {
      method: 'POST', body: JSON.stringify(body),
    });
    haptic.success();
    onChanged();
  }

  const now = Date.now();
  const live = new Date(session.starts_at).getTime() <= now && !past;
  const comingCount = session.bookings.filter((b) => !b.lateCancel).length;
  const isGroup = comingCount >= 2;
  // A pair or group is cancelled as a whole, from the session's ⋯ — a
  // player's own «Отменить» there would call off everyone's lesson.
  const canCancelAll = isGroup && !past && session.status !== 'cancelled'
    && session.bookings.some((b) => b.status === 'booked' && !b.lateCancel);
  const state = session.bookings.length === 0 ? 'free' : comingCount === 0 ? 'late'
    : past ? 'done' : live ? 'live' : 'next';

  /**
   * One note per session, not one per person.
   *
   * A pair working on the same thing used to print it twice, which reads
   * as two different notes at a glance. When everyone in the session is
   * working on the same line it belongs to the session; when they are
   * not, each note stays under the person it is about.
   */
  const focuses = [...new Set(session.bookings.map((b) => b.workingOn).filter(Boolean))] as string[];
  const sharedFocus = focuses.length === 1 ? focuses[0] : null;

  return (
    <div className={`tl__item tl__item--${state}`} id={`sess-${session.id}`}>
      <div className="tl__time">
        <b>{fmtTime(session.starts_at)}</b>
        <span>{fmtTime(session.ends_at)}</span>
      </div>
      <span className="tl__dot" aria-hidden="true" />
      <div className="sess">
      <div className="sess__head">
        <span className="sess__title">
          {session.bookings.length === 0 ? t('co.today.openSlot') : session.title}
          {live && <span className="sess__live">{t('co.today.now')}</span>}
        </span>
        {/* A lesson cancelled outright takes nobody new. */}
        {session.status !== 'cancelled' && (
          <button className="sess__link" onClick={() => setAdding(true)}>{t('co.today.add')}</button>
        )}
        {canCancelAll && (
          <button className="sess__dots" aria-label={t('co.today.sessionMore', { time: fmtTime(session.starts_at) })} aria-expanded={sessMore}
            onClick={() => { haptic.tap(); setSessMore(!sessMore); }}>⋯</button>
        )}
      </div>
      {canCancelAll && sessMore && (
        <div className="sess__actions sess__actions--session">
          <button className="act act--danger" onClick={() => setCancellingAll(true)}>{t('co.today.cancelSession')}</button>
        </div>
      )}

      {sharedFocus && <FocusNote text={sharedFocus} />}

      {session.bookings.map((b) => (
        <div key={b.bookingId} className="sess__person">
          <div className="sess__row">
            <span className="face face--sm">{initials(b.name)}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <button className="sess__name" onClick={() => onOpenClient?.(b.clientId, b.name)}>
                {b.name}
              </button>
              <div className="card__when">
                {b.lateCancel ? <span style={{ color: 'var(--held)' }}>{t('co.today.lateCancel')}</span>
                  : b.status === 'no_show' ? <span style={{ color: 'var(--held)' }}>{t('co.today.noShow')}</span>
                  : b.status === 'attended' ? <span style={{ color: 'var(--court)' }}>{t('co.today.came')}</span>
                  : b.username ? `@${b.username}` : b.phone ?? ''}
                {!b.botStarted && t('co.today.noReminders')}
              </div>
            </div>
            {/* Paid in money: a tap opens «Изменить оплату». */}
            {b.paymentMode === 'cash' && b.paidAt ? (
              <button className="sess__pay sess__pay--edit" onClick={() => { haptic.tap(); setEditing(b); }} aria-label={t('lp.edit')}>
                {t('co.today.paid')} ›
              </button>
            ) : (
            <span className={`sess__pay${b.paymentMode === 'cash' && !b.paidAt ? ' sess__pay--owed' : ''}`}>
              {b.paymentMode === 'cash'
                ? (b.paidAt ? t('co.today.paid')
                  : Number(b.received ?? 0) > 0 ? t('co.today.owes', { amount: Math.round(owedOf(b)) })
                  : Number(b.creditsCharged ?? 0) > 0
                    ? t('co.today.splitPill', { amount: Math.round(owedOf(b)), n: fmtCr(Number(b.creditsCharged)) })
                    : fmtMoney(b.amountDue ?? 0))
                // A late cancel on credits says what really happened: a
                // credit taken, or none to take — never just «Кредиты».
                : b.lateCancel && b.creditTaken != null ? t(b.creditTaken ? 'co.today.creditTaken' : 'co.today.notCharged')
                : t('co.today.credit')}
            </span>
            )}
            {/* Added by mistake to a pair or group: «Убрать из занятия». */}
            {!b.lateCancel && (
              <button className="sess__dots" aria-label={t('co.rm.more', { name: b.name })} aria-expanded={moreFor === b.bookingId}
                onClick={() => { haptic.tap(); setMoreFor(moreFor === b.bookingId ? null : b.bookingId); }}>⋯</button>
            )}
          </div>
          {b.lateCancel && b.paymentMode === 'credits' && b.creditTaken === false && (
            <div className="sess__why">{t('co.today.notChargedWhy')}</div>
          )}
          {/* Partly in credits: «60 € наличными + 0,25 кредита». */}
          {b.paymentMode === 'cash' && Number(b.creditsCharged ?? 0) > 0 && (
            <div className="sess__line sess__line--split">
              {t('co.today.splitLine', {
                amount: Math.round(Number(b.amountDue ?? 0)), n: fmtCr(Number(b.creditsCharged)),
                credits: t.word('book.credit', Number(b.creditsCharged)),
              })}
            </div>
          )}

          {b.workingOn && !sharedFocus && <FocusNote text={b.workingOn} />}
          {b.clientNote && (
            <div className="sess__line sess__line--client"><span>💬</span>{b.clientNote}</div>
          )}

          <CoachNote booking={b} />

          <div className="sess__actions">
            {/* From the moment it starts: marked on court, not remembered afterwards. */}
            {(past || live) && b.status === 'booked' && (
              <>
                <button className="act act--good"
                  onClick={() => act(b, 'attendance', { status: 'attended' })}>{t('co.today.came')}</button>
                <button className="act"
                  onClick={() => act(b, 'attendance', { status: 'no_show' },
                    t('co.today.noShowConfirm'))}>
                  {t('co.today.markNoShow')}
                </button>
              </>
            )}
            {/* From the moment it starts: the coach rates while collecting balls. */}
            {(past || live) && !b.lateCancel && (() => {
              // Rated (saved, or saved and waiting for the lesson's end) reads as rated;
              // a draft not saved yet reads as unfinished.
              const draft = isGroup ? session.notesDraft && !session.notesPublishAt : b.ratingDraft && !b.ratingPublishAt;
              const done = !draft && isRated(b, isGroup ? session : null);
              return (
                <button className={done ? 'act' : 'act act--main'} onClick={() => (isGroup ? setGroupTab(b.bookingId) : setRating(b))}>
                  {draft ? t('co.today.continueRate') : done ? t('co.today.editRate') : t('co.today.rate')}
                </button>
              );
            })()}
            {b.paymentMode === 'cash' && !b.paidAt
              // Cents left on an older lesson are rounding: nothing to collect.
              && !(b.amountDue != null && owedOf(b) < ROUND_OFF) && (
              <button className="act act--cash" onClick={() => setPaying(b)}>
                {t('co.today.gotDots')}
              </button>
            )}
            {/* Only before it starts: a session on court now is not moved. */}
            {!past && !live && b.status === 'booked' && (
              <button className="act" onClick={() => setMoving(b)}>{t('co.profile.move')}</button>
            )}
            {!past && b.status === 'booked' && !isGroup && (
              <button className="act act--danger" onClick={() => setCancelling(b)}>
                {t('co.today.cancel')}
              </button>
            )}
            {moreFor === b.bookingId && isGroup && !b.lateCancel && (
              <button className="act act--danger" onClick={async () => {
                const st = await fetchSeatState(b.bookingId).catch(async (err) => { await alert(t('co.failed', { message: err.message })); return null; });
                if (!st) return;
                const no = removeRefusal(t, b.name, { rated: st.blocked === 'rated', paid: st.blocked === 'paid' });
                if (no) { haptic.error(); await alert(no); return; }
                setRemoving(b);
              }}>{t('co.rm.action')}</button>
            )}
            {/* «Заменить оплату»: this lesson's payment for another's. */}
            {moreFor === b.bookingId && !b.lateCancel && b.status !== 'cancelled' && !b.paidAt && (
              <button className="act" onClick={() => { haptic.tap(); setSwitchFor(b); }}>
                {t('co.sw.action')}
              </button>
            )}
            {moreFor === b.bookingId && !b.lateCancel && (
              <button className="act" onClick={async () => {
                const st = await fetchSeatState(b.bookingId).catch(async (err) => { await alert(t('co.failed', { message: err.message })); return null; });
                if (!st) return;
                // A 1:1: the whole booking goes to the right client.
                if (!isGroup) { setReassigning({ booking: b, moves: st.moves, solo: true }); return; }
                // Saved work, and the session has started: the whole
                // booking moves to the right client instead.
                if (st.blocked && st.started) { setReassigning({ booking: b, moves: st.moves }); return; }
                const no = removeRefusal(t, b.name, { rated: st.blocked === 'rated', paid: st.blocked === 'paid' });
                if (no) { haptic.error(); await alert(no); return; }
                setReplacing(b);
              }}>{t('co.rp.action')}</button>
            )}
          </div>
          {(past || live) && !b.lateCancel && !(isGroup ? session.notesDraft && !session.notesPublishAt : b.ratingDraft && !b.ratingPublishAt)
            && isRated(b, isGroup ? session : null) && (
            // Only the note for everyone, nothing of their own: «Заметка ✓», not «Оценено ✓».
            onlyShared(b, isGroup ? session : null) ? (
              <div className="sess__rated"><b>{t('co.today.noteOnly')}</b></div>
            ) : (
              <div className="sess__rated">
                <b>{t('co.today.rated')}</b>
                {(b.ratings ?? []).length > 0 && <span>{(b.ratings ?? []).map((r) => `${axisName(r)} ${r.score}`).join(' · ')}</span>}
                {(b.hasNote || (isGroup && session.commonNote)) && <i className="sess__chip">{t('co.today.chipNote')}</i>}
                {b.hasHomework && <i className="sess__chip">{t('co.today.chipHw')}</i>}
              </div>
            )
          )}
          {!isGroup && (b.ratingPublishAt ? (
            <div className="sess__draft">{t('co.today.rateWillShow', { time: fmtTime(b.ratingPublishAt) })}</div>
          ) : b.ratingDraft && (
            <div className="sess__draft">{t('co.today.rateDraft')}</div>
          ))}
        </div>
      ))}
      {/* A pair or group: one line for the whole session's notes. */}
      {isGroup && (session.notesPublishAt ? (
        <div className="sess__draft sess__draft--ok">✓ {t('co.today.notesWillShow', { time: fmtTime(session.notesPublishAt) })}</div>
      ) : session.notesDraft && (
        <div className="sess__draft">{t('co.today.rateDraft')}</div>
      ))}
      </div>

      {removing && (
        <RemoveSheet bookingId={removing.bookingId} name={removing.name} startsAt={session.starts_at}
          inBot={removing.botStarted} bookedAt={removing.bookedAt}
          onClose={() => { setRemoving(null); setMoreFor(null); onChanged(); }} onDone={onChanged} />
      )}

      {reassigning && (
        <AddClientSheet session={session} reassigning={reassigning}
          onClose={() => { setReassigning(null); setMoreFor(null); onChanged(); }} onDone={onChanged} />
      )}

      {replacing && (
        <AddClientSheet session={session} replacing={replacing}
          onClose={() => { setReplacing(null); setMoreFor(null); onChanged(); }} onDone={onChanged} />
      )}

      {adding && (
        <AddClientSheet session={session}
          onClose={() => setAdding(false)} onDone={onChanged} />
      )}

      {rating && (
        <RatingSheet booking={rating}
          onClose={() => { setRating(null); onChanged(); }} onDone={onChanged} />
      )}

      {groupTab && (
        <GroupNotesSheet sessionId={session.id} startTab={groupTab}
          onClose={() => { setGroupTab(null); onChanged(); }} onDone={onChanged} />
      )}

      {switchFor && (
        <PaySwitchSheet bookingId={switchFor.bookingId}
          onClose={() => setSwitchFor(null)}
          onDone={() => { setSwitchFor(null); onChanged(); }} />
      )}
      {paying && (
        <LessonPaySheet bookingId={paying.bookingId} name={paying.name}
          onClose={() => { setPaying(null); onChanged(); }} onDone={onChanged} />
      )}
      {editing && (
        <EditPaymentSheet bookingId={editing.bookingId} name={editing.name}
          onClose={() => { setEditing(null); onChanged(); }} onDone={onChanged} />
      )}

      {/* One lesson only from Today; a whole series moves from the client's profile. */}
      {moving && (
        <MoveSheet booking={{ booking_id: moving.bookingId, starts_at: session.starts_at }}
          name={moving.name} isSeries={false}
          onClose={() => { setMoving(null); onChanged(); }} onDone={onChanged} />
      )}

      {cancellingAll && (
        <CancelSheet
          title={t('co.today.cancelSessionTitle', { time: fmtTime(session.starts_at) })}
          subtitle={t('co.today.cancelSessionBody', {
            day: fmtLongDay(new Date(session.starts_at)), time: fmtTime(session.starts_at),
            names: session.bookings.filter((b) => b.status === 'booked' && !b.lateCancel).map((b) => b.name).join(', '),
          })}
          warning={session.bookings.some((b) => b.status === 'booked' && !b.botStarted) ? t('co.today.cancelNoMessage') : undefined}
          confirmLabel={t('co.today.cancelDo')}
          onClose={() => { setCancellingAll(false); setSessMore(false); }}
          onConfirm={async (reason) => {
            await api(`/api/coach/sessions/${session.id}/cancel`, {
              method: 'POST', body: JSON.stringify({ reason }),
            });
            setCancellingAll(false);
            onChanged();
          }}
        />
      )}

      {cancelling && (
        <CancelSheet
          title={t('co.today.cancelTitle', { name: cancelling.name })}
          subtitle={t('co.today.cancelBody', {
            day: fmtLongDay(new Date(session.starts_at)), time: fmtTime(session.starts_at),
          })}
          warning={!cancelling.botStarted ? t('co.today.cancelNoMessage') : undefined}
          confirmLabel={t('co.today.cancelDo')}
          onClose={() => { setCancelling(null); onChanged(); }}
          onConfirm={async (reason) => {
            await api(`/api/coach/bookings/${cancelling.bookingId}/cancel`, {
              method: 'POST', body: JSON.stringify({ reason }),
            });
            setCancelling(null);
            onChanged();
          }}
        />
      )}
    </div>
  );
}

/**
 * The rain sheet.
 *
 * Shows who it affects BEFORE doing anything — a coach tapping this is
 * already having a bad morning and shouldn't discover the damage after.
 * Blocking the time is on by default: cancel for rain without blocking
 * and someone rebooks into the storm ten minutes later.
 */
function DisruptSheet({ day, onClose, onDone }: {
  day: string; onClose: () => void; onDone: () => void;
}) {
  const t = useT();
  const [reason, setReason] = useState('rain');
  const [note, setNote] = useState('');
  const [block, setBlock] = useState(true);
  const [preview, setPreview] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const from = new Date(`${day}T00:00:00`);
  const to = new Date(`${day}T23:59:59`);

  useEffect(() => {
    api<any>(`/api/coach/disrupt/preview?from=${from.toISOString()}&to=${to.toISOString()}`)
      .then(setPreview).catch(() => setPreview({ count: 0, affected: [], unreachable: [] }));
  }, [day]);

  async function go() {
    const ok = await confirm(t('co.today.cancelDayConfirm', {
      n: preview.count, sessions: t.word('co.today.sessions', preview.count),
      day: fmtLongDay(new Date(`${day}T12:00:00`)),
    }));
    if (!ok) return;

    setBusy(true);
    try {
      const res = await api<{ cancelled: number; refunded: number }>('/api/coach/disrupt', {
        method: 'POST',
        body: JSON.stringify({
          from: from.toISOString(), to: to.toISOString(),
          reason, note: note || null, block,
        }),
      });
      haptic.success();
      await alert(
        t('co.today.cancelDayDone', {
          n: res.cancelled, r: res.refunded, credits: t.word('book.credit', res.refunded),
        }) + (block ? t('co.today.cancelDayBlocked') : ''));
      onDone();
      onClose();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.failed', { message: err.message }));
    } finally {
      setBusy(false);
    }
  }

  return createPortal(
    <div className="sheet">
      <div className="sheet__panel">
        <div className="sheet__title">
          {t('co.today.cancelDayTitle', { day: fmtLongDay(new Date(`${day}T12:00:00`)) })}
        </div>

        {preview === null ? (
          <div className="skeleton" />
        ) : preview.count === 0 ? (
          <div className="card__when">{t('co.today.nobodyToTell')}</div>
        ) : (
          <>
            <div className="card__when">
              {t('co.today.affected', {
                n: preview.count, sessions: t.word('co.today.sessions', preview.count),
              })}{' '}
              {preview.affected.map((a: any) => a.name).join(', ')}
            </div>
            {preview.unreachable.length > 0 && (
              <div className="warnline">
                {t('co.today.unreachable', {
                  names: preview.unreachable.map((u: any) => u.name).join(', '),
                })}
                {preview.unreachable.some((u: any) => u.phone) &&
                  ` (${preview.unreachable.filter((u: any) => u.phone).map((u: any) => u.phone).join(', ')})`}
              </div>
            )}
          </>
        )}

        <div className="sheet__label">{t('co.today.why')}</div>
        <div className="repeat__opts">
          {REASONS.map(([id, key]) => (
            <button key={id} className="chip" aria-pressed={reason === id}
              onClick={() => { haptic.tap(); setReason(id); }}>{t(key)}</button>
          ))}
        </div>

        <input className="paybar__opt"
          style={{ width: '100%', textAlign: 'left', margin: '10px 0' }}
          placeholder={t('co.today.reasonLabel')}
          value={note} onChange={(e) => setNote(e.target.value)} />

        <label className="checkline">
          <input type="checkbox" checked={block} onChange={(e) => setBlock(e.target.checked)} />
          <span>
            {t('co.today.blockLabel')}
            <small>{t('co.today.blockDay')}</small>
          </span>
        </label>

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button className="paybar__opt" data-back style={{ flex: 1 }} onClick={onClose}>{t('co.pkg.back')}</button>
          <button className="paybar__opt"
            style={{ flex: 2, background: 'var(--danger)', color: '#fff', borderColor: 'var(--danger)' }}
            disabled={busy || preview === null} onClick={go}>
            {busy ? t('co.today.cancelling') : t('co.today.cancelAll', { n: preview?.count ?? 0 })}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
